import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import type { CSSSettings } from "shared-types";
import type { InDesignKerningMethod } from "shared-types/indesign";
import { clampInDesignKerningValue, clampInDesignTracking } from "shared-utils/utils/indesignRange";
import { OverwriteScope } from "shared-utils/enums/overwrites";
import { EditableType, LegacyEditableTypes } from "shared-utils/enums/editables";

import { useEditor } from "@/composables/useEditor";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useOverwrite } from "@/composables/useOverwrite";

import getDomValue from "@/utils/getDomValue";
import {
    EditablePhotoshopSettings,
    EditableSettingProperty,
    EditableTranslateMethod,
    type EditablePhotoshopSettingsValues
} from "@/enums/editables";
import { useStore } from "vuex";
import { type RootState } from "@/store/types";
import { EditorAction } from "@/store/modules/editor";

import * as editableUtils from "@/utils/editables";

type CssProperty = NonNullable<keyof CSSSettings>;

type Options = {
    onSettingsChange: (settings: CSSSettings | null) => void;
    // Used for HTML5 banners
    computedStyle?: CSSStyleDeclaration;
    boundingClientRect?: DOMRect;
};

export function useEditableSettings({ onSettingsChange, computedStyle, boundingClientRect }: Options) {
    const store = useStore<RootState>();
    const { editedEditable, fonts, isCampaignPSD, isCampaignIndesign, selectedLanguage } = useEditor();
    const { getOverwrite } = useOverwrite();
    const { getOwnSettings } = useEditableMethods();

    const scopeLevel = computed(() =>
        editedEditable.value?.masterTemplateId ? OverwriteScope.EditableGroupTemplate : OverwriteScope.EditableGroup
    );

    const overwrite = computed(() => {
        if (!editedEditable.value) {
            return null;
        }

        return getOverwrite(
            editedEditable.value.editable,
            editedEditable.value.groupsAffectingEditable,
            editedEditable.value.masterTemplateId
        );
    });

    /**
     * An InDesign size inherits the group's typography key by key. Editing it from what it
     * resolves to would store every inherited value as its own, and changes for all sizes would
     * stop reaching it.
     */
    const inheritsPerKey = computed(
        () => isCampaignIndesign.value && scopeLevel.value === OverwriteScope.EditableGroupTemplate
    );

    const settings = computed(() => {
        if (!overwrite.value) {
            return null;
        }

        if (inheritsPerKey.value && editedEditable.value) {
            const { editable, groupsAffectingEditable, masterTemplateId } = editedEditable.value;

            return getOwnSettings(editable, groupsAffectingEditable, masterTemplateId) ?? null;
        }

        return editableUtils.getEditableSettings(overwrite.value);
    });

    const parentSettings = computed(() => {
        if (scopeLevel.value !== OverwriteScope.EditableGroupTemplate) {
            return {};
        }

        // What the size really inherits, in the language being edited
        if (inheritsPerKey.value && editedEditable.value) {
            const { editable, groupsAffectingEditable } = editedEditable.value;

            return getOverwrite(editable, groupsAffectingEditable, null)?.settings;
        }

        return overwrite.value?.settingsFromScope?.[OverwriteScope.EditableGroup];
    });

    const initialSettings: CSSSettings = settings.value ?? {};

    // FontStyle
    const fontSize = ref<string | undefined>(initialSettings.fontSize);
    const fontColor = ref<string | undefined>(initialSettings.fontColor);
    const fontFamily = ref<string | undefined>(initialSettings.fontFamily?._id);
    const fontStyle = ref<string | undefined>(initialSettings.fontStyle);
    const letterSpacing = ref<number | undefined>(initialSettings.letterSpacing);
    const lineHeight = ref<number | undefined>(initialSettings.lineHeight);
    const textAlignment = ref<string | undefined>(initialSettings.textAlignment);
    const fontWeight = ref<string | undefined>(initialSettings.fontWeight);

    // AfterEffects font
    const font = ref<string | undefined>(initialSettings.font);

    const tracking = ref<number | undefined>(initialSettings.tracking);
    const kerningMethod = ref<InDesignKerningMethod | undefined>(initialSettings.kerningMethod);
    const kerningValue = ref<number | undefined>(initialSettings.kerningValue);

    const offsetX = ref<number | undefined>(initialSettings.offsetX);
    const offsetY = ref<number | undefined>(initialSettings.offsetY);

    // ElementPosition
    const width = ref<number | undefined>(initialSettings.width);
    const height = ref<number | undefined>(initialSettings.height);
    const top = ref<number | undefined>(initialSettings.top);
    const left = ref<number | undefined>(initialSettings.left);
    const scale = ref<number | undefined>(initialSettings.scale);
    const translationMethod = ref<"relative" | "transform" | "margin" | "background-position" | undefined>(
        initialSettings.translationMethod
    );
    const preserveAspectRatio = ref(true);
    const aspectRatio = ref(1);
    let unsubscribe: null | (() => void) = null;
    let isRestoringSettings = false;

    onMounted(() => {
        unsubscribe = store.subscribeAction({
            after: action => {
                if (action.type === EditorAction.ClearLocalChanges) {
                    restoreSettings();
                }
            }
        });
    });

    onUnmounted(() => {
        unsubscribe?.();
        unsubscribe = null;
    });

    watch(selectedLanguage, () => {
        restoreSettings();
    });

    // The overlay writes offsets without going through these inputs
    watch(
        () => [settings.value?.offsetX, settings.value?.offsetY],
        ([nextOffsetX, nextOffsetY]) => {
            if (isRestoringSettings) {
                return;
            }

            if (nextOffsetX !== offsetX.value) {
                offsetX.value = nextOffsetX;
            }

            if (nextOffsetY !== offsetY.value) {
                offsetY.value = nextOffsetY;
            }
        }
    );

    // The editor's own input clamps too, but it runs after the field has taken the typed value
    watch(tracking, value => {
        const clamped = clampInDesignTracking(value);

        if (value !== undefined && clamped !== value) {
            tracking.value = clamped;
        }
    });

    watch(kerningValue, value => {
        const clamped = clampInDesignKerningValue(value);

        if (value !== undefined && clamped !== value) {
            kerningValue.value = clamped;
        }
    });

    watch(translationMethod, () => {
        if (!isRestoringSettings) {
            top.value = undefined;
            left.value = undefined;
        }
    });

    watch(
        () => ({
            fontSize: fontSize.value,
            fontColor: fontColor.value,
            fontFamily: fontFamily.value ? fonts.value[fontFamily.value] : undefined,
            fontStyle: fontStyle.value,
            letterSpacing: letterSpacing.value,
            lineHeight: lineHeight.value,
            textAlignment: textAlignment.value,
            fontWeight: fontWeight.value,
            font: font.value,
            tracking: tracking.value,
            // Clearing the select emits "" before undefined, and a stored "" would block inheriting
            kerningMethod: kerningMethod.value || undefined,
            kerningValue: kerningValue.value,
            offsetX: offsetX.value,
            offsetY: offsetY.value,
            width: width.value,
            height: height.value,
            top: top.value,
            left: left.value,
            scale: scale.value,
            translationMethod: translationMethod.value
        }),
        (newValues, oldValues) => {
            updateCssSettings(newValues, oldValues);
        },
        { deep: false, immediate: true }
    );

    watch([width, height], ([newWidth, newHeight], [oldWidth, oldHeight]) => {
        if (!isCampaignPSD.value || (newWidth === oldWidth && newHeight === oldHeight)) {
            return;
        }

        const editableTypes = [
            LegacyEditableTypes.BackgroundImage,
            EditableType.Image,
            EditableType.Textarea,
            EditableType.Text
        ];

        if (preserveAspectRatio.value && editableTypes.includes(editedEditable.value?.editable.type ?? "")) {
            if (newWidth !== oldWidth) {
                height.value = newWidth ? parseInt((newWidth / aspectRatio.value).toFixed(0)) : undefined;
            }
            if (newHeight !== oldHeight) {
                width.value = newHeight ? parseInt((newHeight * aspectRatio.value).toFixed(0)) : undefined;
            }
        }
    });

    watch(
        editedEditable,
        () => {
            if (!isCampaignPSD.value) {
                return;
            }

            updateAspectRatio();
        },
        { immediate: true }
    );

    watch(preserveAspectRatio, newValue => {
        if (!isCampaignPSD.value || !newValue) {
            return;
        }

        width.value = height.value ? parseInt((height.value * aspectRatio.value).toFixed(0)) : undefined;
    });

    function updateCssSettings(newSettings: CSSSettings, oldSettings: CSSSettings | undefined) {
        const changedEntries = Object.entries(newSettings).filter(([key, value]) => {
            if (key === "fontFamily") {
                const newFontId = (value as CSSSettings["fontFamily"])?._id;
                const oldFontId = oldSettings?.fontFamily?._id;

                const settingsFontId = settings.value?.fontFamily?._id;

                // Check if the font family has changed and is different from both the old settings and the current overwrite settings
                return newFontId !== oldFontId && newFontId !== settingsFontId;
            }

            return (
                value !== oldSettings?.[key as keyof typeof newSettings] &&
                // check for restore changes button to be sure we don't add again the settings to overwrite
                value !== settings.value?.[key as keyof typeof settings.value]
            );
        });

        if (!changedEntries.length) {
            return;
        }

        const merged = {
            ...(settings.value ?? {}),
            ...Object.fromEntries(changedEntries)
        } as Record<string, unknown>;

        if (
            scopeLevel.value === OverwriteScope.EditableGroupTemplate &&
            changedEntries.some(([key]) => key === "fontFamily") &&
            merged.fontFamily === undefined
        ) {
            merged.fontFamily = null;
        }

        const newData = Object.fromEntries(Object.entries(merged).filter(([, value]) => value !== undefined));
        const settingToUpdate = Object.keys(newData).length ? newData : null;

        onSettingsChange(settingToUpdate);
    }

    function updateAspectRatio() {
        const editableType = editedEditable.value?.editable.type;

        if ((editableType === EditableType.Text || editableType === EditableType.Textarea) && !settings.value?.width) {
            aspectRatio.value = 1;
            width.value = 100;
            height.value = 100;

            return;
        }

        if (editableType === EditableType.Image || editableType === LegacyEditableTypes.BackgroundImage) {
            const bounds = editedEditable.value?.editable.layers?.[0]?.value?.bounds;

            if (bounds) {
                const { width, height } = bounds;

                aspectRatio.value = width / height;

                return;
            }
        }

        aspectRatio.value = 1;
    }

    function resetStyles(component: "FontStyle" | "ElementPosition") {
        if (component === "FontStyle") {
            font.value = undefined;
            fontSize.value = undefined;
            fontColor.value = undefined;
            fontFamily.value = undefined;
            fontStyle.value = undefined;
            letterSpacing.value = undefined;
            lineHeight.value = undefined;
            textAlignment.value = undefined;
            fontWeight.value = undefined;
            tracking.value = undefined;
            kerningMethod.value = undefined;
            kerningValue.value = undefined;

            return;
        }

        // component === "ElementPosition"
        top.value = undefined;
        left.value = undefined;
        width.value = undefined;
        height.value = undefined;
        scale.value = undefined;
        translationMethod.value = undefined;
        offsetX.value = undefined;
        offsetY.value = undefined;
    }

    function isSuffixVisible(property: CssProperty) {
        if (!computedStyle) {
            return false;
        }

        const domValue = getDomValue(computedStyle, property, boundingClientRect);

        if (domValue === undefined) {
            return true;
        }

        if (Number.isNaN(parseFloat(domValue))) {
            return false;
        }

        return domValue.endsWith("px");
    }

    function getPlaceholderValue(property: CssProperty) {
        const parentSettingsValue = parentSettings.value?.[property];

        if (parentSettingsValue === EditableTranslateMethod.Transform) {
            getPositionPlaceholderValue(property);
        }

        if (property === EditableSettingProperty.TranslationMethod) {
            return parentSettingsValue ?? EditableTranslateMethod.Relative;
        }

        if (typeof parentSettingsValue === "number" && !Number.isNaN(parentSettingsValue)) {
            return parentSettingsValue.toString();
        }

        if (typeof parentSettingsValue === "string") {
            const parsed = parseFloat(parentSettingsValue);

            if (!Number.isNaN(parsed)) {
                return parsed.toString();
            }
        }

        if (computedStyle && boundingClientRect) {
            const domValue = getDomValue(computedStyle, property, boundingClientRect);

            if (domValue !== undefined) {
                const parsedValue = parseFloat(domValue);

                if (!Number.isNaN(parsedValue)) {
                    return parsedValue.toString();
                }

                return domValue;
            }
        }

        return "";
    }

    // As stored, where the placeholder reads numbers only
    function getInheritedValue<K extends CssProperty>(property: K): CSSSettings[K] | undefined {
        return parentSettings.value?.[property];
    }

    function getPsdPlaceholderValue(placeholder: EditablePhotoshopSettingsValues) {
        const alignmentPlaceholders: Record<string, string> = {
            left: "Left",
            right: "Right",
            center: "Center",
            justify_left: "Justify Left",
            justify_center: "Justify Center",
            justify_right: "Justify Right",
            justify_all: "Justify All"
        };

        if (placeholder === EditablePhotoshopSettings.textAlignment) {
            const placeholderValue =
                editedEditable.value?.editable.layers?.[0]?.value?.text.paragraphStyles[0]?.[placeholder];

            return placeholderValue ? (alignmentPlaceholders[placeholderValue] ?? placeholderValue) : "";
        }

        return editedEditable.value?.editable.layers?.[0]?.value?.text.characterStyles?.[0]?.[placeholder] ?? "";
    }

    function getPositionPlaceholderValue(property: CssProperty) {
        if (!computedStyle) {
            return;
        }

        const transformStyle = getDomValue(computedStyle, "transform", boundingClientRect);

        if (!transformStyle) {
            return;
        }

        if (transformStyle === "none") {
            return "0";
        }

        if (transformStyle.startsWith("matrix")) {
            const [, , , , translateX, translateY] = transformStyle.split(",");
            if (property === EditableSettingProperty.Left) {
                return translateX;
            }

            if (property === EditableSettingProperty.Top) {
                return translateY?.replace(")", "");
            }
        }
    }

    function restoreSettings() {
        isRestoringSettings = true;

        const currentSettings: CSSSettings = settings.value ?? {};

        fontSize.value = currentSettings.fontSize;
        font.value = currentSettings.font;
        fontColor.value = currentSettings.fontColor;
        fontFamily.value = currentSettings.fontFamily?._id;
        fontStyle.value = currentSettings.fontStyle;
        letterSpacing.value = currentSettings.letterSpacing;
        lineHeight.value = currentSettings.lineHeight;
        textAlignment.value = currentSettings.textAlignment;
        fontWeight.value = currentSettings.fontWeight;
        tracking.value = currentSettings.tracking;
        kerningMethod.value = currentSettings.kerningMethod;
        kerningValue.value = currentSettings.kerningValue;
        offsetX.value = currentSettings.offsetX;
        offsetY.value = currentSettings.offsetY;
        top.value = currentSettings.top;
        left.value = currentSettings.left;
        width.value = currentSettings.width;
        height.value = currentSettings.height;
        scale.value = currentSettings.scale;
        translationMethod.value = currentSettings.translationMethod;

        nextTick(() => {
            isRestoringSettings = false;
        }).catch(() => undefined);
    }

    return {
        isSuffixVisible,
        getPlaceholderValue,
        getInheritedValue,
        getPsdPlaceholderValue,
        resetStyles,

        fontSize,
        fontColor,
        fontFamily,
        fontStyle,
        letterSpacing,
        lineHeight,
        textAlignment,
        fontWeight,
        font,
        tracking,
        kerningMethod,
        kerningValue,
        offsetX,
        offsetY,

        width,
        height,
        top,
        left,
        scale,
        translationMethod,
        preserveAspectRatio,
        aspectRatio
    };
}
